import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { poli, qs } from './poliApi';

/**
 * P-9 Estadisticas: por proyecto, por motivo, asistencia por periodo y resultado por pregunta.
 * El cliente solo cuenta lo de su compania (lo filtra el servidor). Lo de las preguntas son
 * totales: el contenido de los informes no sale del servidor.
 */
const z = (n) => String(n).padStart(2, '0');
const dia = (d) => `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
const hoy = new Date();
const AGRUPAR = [{ label: 'Dia', value: 'dia' }, { label: 'Semana', value: 'semana' }, { label: 'Mes', value: 'mes' }];
const VERDE = '#2e7d32';
const ROJO = '#c62828';
const GRIS = '#78909c';
const PALETA = ['#1565c0', '#ef6c00', '#6a1b9a', '#00838f', '#ad1457', '#558b2f', '#4e342e', '#5c6bc0'];

const PoliEstadisticas = () => {
    const toast = useRef(null);
    const [cfg, setCfg] = useState(null);
    const [f, setF] = useState({ desde: dia(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1)), hasta: dia(hoy), companiaId: null, agrupar: 'semana' });
    const [d, setD] = useState(null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => { poli('/config').then(setCfg).catch(() => {}); }, []);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            setD(await poli(`/estadisticas?${qs(f)}`));
        } catch (e) {
            toast.current && toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
        } finally {
            setCargando(false);
        }
    }, [f]);

    useEffect(() => { cargar(); }, [cargar]);

    const companias = (cfg && cfg.companias) || [];
    const opcionesBarras = { maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } } };
    const opcionesPreguntas = { ...opcionesBarras, indexAxis: 'y' };

    const asistencia = d && {
        labels: d.asistencia.map((r) => r.periodo),
        datasets: [
            { label: 'Asistio', data: d.asistencia.map((r) => r.asistio), backgroundColor: VERDE },
            { label: 'No asistio', data: d.asistencia.map((r) => r.no_asistio), backgroundColor: ROJO },
            { label: 'No realizada', data: d.asistencia.map((r) => r.no_realizada), backgroundColor: GRIS }
        ]
    };
    const motivos = d && {
        labels: d.porMotivo.map((r) => r.motivo),
        datasets: [{ data: d.porMotivo.map((r) => r.total), backgroundColor: d.porMotivo.map((r, i) => PALETA[i % PALETA.length]) }]
    };
    const top = d ? d.porPregunta.slice(0, 15) : [];
    const preguntas = d && {
        labels: top.map((r) => (r.pregunta.length > 60 ? r.pregunta.slice(0, 57) + '...' : r.pregunta)),
        datasets: [
            { label: 'Paso', data: top.map((r) => r.paso), backgroundColor: VERDE },
            { label: 'No paso', data: top.map((r) => r.no_paso), backgroundColor: ROJO }
        ]
    };
    const campo = { display: 'flex', flexDirection: 'column', gap: 4 };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12"><h4 className="m-0">Estadisticas de poligrafia</h4></div>
            <div className="col-12">
                <div className="card mb-0 flex flex-wrap align-items-end gap-3">
                    <label style={campo} htmlFor="est-desde">Desde
                        <input id="est-desde" type="date" className="p-inputtext" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} />
                    </label>
                    <label style={campo} htmlFor="est-hasta">Hasta
                        <input id="est-hasta" type="date" className="p-inputtext" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} />
                    </label>
                    {companias.length > 1 && (
                        <label style={campo} htmlFor="est-compania">Compania
                            <Dropdown inputId="est-compania" value={f.companiaId} options={companias} optionLabel="nombre" optionValue="compania_id"
                                showClear placeholder="Todas" onChange={(e) => setF({ ...f, companiaId: e.value })} style={{ minWidth: '13rem' }} />
                        </label>
                    )}
                    <div style={campo}>Agrupar por
                        <SelectButton value={f.agrupar} options={AGRUPAR} onChange={(e) => e.value && setF({ ...f, agrupar: e.value })} />
                    </div>
                    <Button label="Actualizar" icon="pi pi-refresh" className="p-button-outlined" onClick={cargar} loading={cargando} />
                </div>
            </div>
            {d && (
                <>
                    <div className="col-12">
                        <div className="card mb-0">
                            <h5>Por compania y proyecto</h5>
                            <DataTable value={d.porProyecto} size="small" emptyMessage="Sin pruebas en el rango" responsiveLayout="scroll">
                                <Column header="Compania" field="compania" />
                                <Column header="Proyecto" field="proyecto" />
                                <Column header="Total" field="total" />
                                <Column header="En curso" field="en_curso" />
                                <Column header="Realizadas" field="realizadas" />
                                <Column header="Con informe" field="con_informe" />
                                <Column header="No asistio" field="no_asistio" />
                                <Column header="No realizada" field="no_realizada" />
                                <Column header="Anuladas" field="anuladas" />
                            </DataTable>
                        </div>
                    </div>
                    <div className="col-12 lg:col-8">
                        <div className="card mb-0 h-full">
                            <h5>Asistencia por {AGRUPAR.find((a) => a.value === f.agrupar).label.toLowerCase()}</h5>
                            {d.asistencia.length ? <div style={{ height: 300 }}><Chart type="bar" data={asistencia} options={opcionesBarras} style={{ height: '100%' }} /></div>
                                : <p className="text-600">Sin citas en el rango</p>}
                        </div>
                    </div>
                    <div className="col-12 lg:col-4">
                        <div className="card mb-0 h-full">
                            <h5>Por motivo</h5>
                            {d.porMotivo.length ? <div style={{ height: 300 }}><Chart type="doughnut" data={motivos} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} style={{ height: '100%' }} /></div>
                                : <p className="text-600">Sin pruebas en el rango</p>}
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="card mb-0">
                            <h5>Resultado por pregunta <span className="text-600 text-sm">({d.informesLeidos} informes)</span></h5>
                            {top.length ? <div style={{ height: Math.max(160, top.length * 34) }}><Chart type="bar" data={preguntas} options={opcionesPreguntas} style={{ height: '100%' }} /></div>
                                : <p className="text-600">Todavia no hay informes enviados en el rango</p>}
                            {d.porPregunta.length > top.length && <small className="text-600">Se muestran las {top.length} preguntas mas usadas de {d.porPregunta.length}.</small>}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PoliEstadisticas;
